import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Plus, AlertCircle, Clock, Search, X, AlertTriangle,
  CheckCircle2, ChevronRight, History, Timer, Filter,
  ShieldAlert, ShieldCheck, ShieldX, CalendarClock, Info, Gavel,
  FileStack, Send
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api';
import PrintScheduleButton from '@/components/PrintScheduleButton';
import EventApprovalWidget from '@/components/EventApprovalWidget';

const FilterBanner = ({ filterName, count, onClear, color = 'amber' }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    red: 'bg-red-50 border-red-200 text-red-800',
  };
  return (
    <div className={`flex items-center justify-between p-3 ${colors[color]} border rounded-lg mb-4`}>
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4" />
        <span className="text-sm font-medium">
          Filtered by: <span className="font-bold">{filterName}</span>
        </span>
        <Badge variant="secondary" className="text-xs">{count} items</Badge>
      </div>
      <Button variant="ghost" size="sm" onClick={onClear} className="hover:bg-white/50">
        <X className="h-4 w-4 mr-1" /> Clear Filter
      </Button>
    </div>
  );
};

// Check if a claim has been submitted using any available field
const isClaimSubmitted = (event) => {
  if (event.claim_submitted_at) return true;
  if (event.status === 'claim_submitted') return true;
  const cs = (event.claim_status || '').toLowerCase();
  return ['submitted', 'in_adjudication', 'under_review', 'adjudication'].includes(cs);
};

const getStatusBadge = (status) => {
  const styles = {
    draft: 'bg-slate-100 text-slate-700 border-slate-200',
    pending_classification: 'bg-amber-100 text-amber-800 border-amber-300',
    clause_assigned: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    ready_for_notice: 'bg-teal-100 text-teal-700 border-teal-200',
    notice_created: 'bg-blue-100 text-blue-700 border-blue-200',
    notice_issued: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    claim_created: 'bg-violet-100 text-violet-700 border-violet-200',
    submitted: 'bg-purple-100 text-purple-700 border-purple-200',
    claim_submitted: 'bg-purple-100 text-purple-700 border-purple-200',
    approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
    deemed_rejected: 'bg-red-100 text-red-800 border-red-300',
    deemed_accepted: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    referable_to_dab: 'bg-amber-100 text-amber-800 border-amber-300',
  };
  return styles[status] || styles.draft;
};

// Derive a display-friendly status label from the event data
const getEventDisplayStatus = (event) => {
  // If claim has been submitted/shared
  if (event.claim_submitted_at || event.status === 'claim_submitted') {
    return { status: 'claim_submitted', label: 'CLAIM SUBMITTED' };
  }
  // If claim_status on the event itself says Submitted/In Adjudication (from claim sync)
  const cs = (event.claim_status || '').toLowerCase();
  if (cs === 'submitted' || cs === 'in_adjudication' || cs === 'under_review' || cs === 'adjudication') {
    return { status: 'claim_submitted', label: cs === 'in_adjudication' ? 'IN ADJUDICATION' : 'CLAIM SUBMITTED' };
  }
  // If a claim exists (draft) but not yet submitted
  if (event.claim_id || (event.claim_status && cs === 'draft')) {
    return { status: 'claim_created', label: 'CLAIM CREATED' };
  }
  // If notice has been shared/issued (has communication log or status indicates it)
  if (event.notice_id && event.notice_shared) {
    return { status: 'notice_issued', label: 'NOTICE ISSUED' };
  }
  // If notice exists
  if (event.notice_id || event.status === 'notice_created') {
    return { status: 'notice_created', label: 'NOTICE CREATED' };
  }
  // Pending contractual classification
  if (event.status === 'pending_classification' || event.notice_generation_locked) {
    return { status: 'pending_classification', label: 'PENDING CLASSIFICATION' };
  }
  // Clause assigned
  if (event.status === 'clause_assigned') {
    return { status: 'clause_assigned', label: 'CLAUSE ASSIGNED' };
  }
  // Ready for notice
  if (event.status === 'ready_for_notice') {
    return { status: 'ready_for_notice', label: 'READY FOR NOTICE' };
  }
  // Ceased states are handled separately
  if (event.cease_status === 'ceased_confirmed' || event.cease_status === 'ceased_pending') {
    return null; // handled by cease badges
  }
  // Default
  return { status: event.status || 'draft', label: (event.status || 'draft').replace(/_/g, ' ').toUpperCase() };
};

// Compliance status badge for time-bar tracking
const ComplianceBadge = ({ flag, daysRemaining, daysOverdue, label }) => {
  if (!flag) return null;
  const config = {
    submitted: { className: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: ShieldCheck, text: `${label} Submitted` },
    time_barred: { className: 'bg-red-100 text-red-800 border-red-300', icon: ShieldX, text: `TIME BARRED${daysOverdue ? ` (${daysOverdue}d overdue)` : ''}` },
    urgent: { className: 'bg-red-50 text-red-700 border-red-200', icon: ShieldAlert, text: `${daysRemaining}d remaining` },
    warning: { className: 'bg-amber-50 text-amber-700 border-amber-200', icon: ShieldAlert, text: `${daysRemaining}d remaining` },
    ok: { className: 'bg-sky-50 text-sky-700 border-sky-200', icon: CalendarClock, text: `${daysRemaining}d remaining` },
  };
  const c = config[flag] || config.ok;
  const Icon = c.icon;
  return (
    <Badge data-testid={`compliance-badge-${flag}`} className={`${c.className} border text-[10px] font-semibold tracking-wide`}>
      <Icon className="h-3 w-3 mr-1" />
      {c.text}
    </Badge>
  );
};

// Risk flag pills with tooltip
const RiskFlags = ({ flags = [] }) => {
  if (!flags.length) return null;
  const severityColors = {
    CRITICAL: 'bg-red-600 text-white',
    HIGH: 'bg-orange-500 text-white',
    MODERATE: 'bg-amber-400 text-amber-900',
  };
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex flex-wrap gap-1 mt-2" data-testid="risk-flags-container">
        {flags.map((rf, idx) => (
          <Tooltip key={idx}>
            <TooltipTrigger asChild>
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider cursor-help ${severityColors[rf.severity] || severityColors.MODERATE}`}>
                <AlertTriangle className="h-2.5 w-2.5" />
                {rf.flag?.replace(/_/g, ' ')}
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs text-xs">
              <p>{rf.text}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
};

// Deadline info row
const DeadlineInfo = ({ label, deadline, statusFlag }) => {
  if (!deadline) return null;
  const date = new Date(deadline);
  const isOverdue = statusFlag === 'time_barred';
  return (
    <div className={`flex items-center gap-1.5 text-[11px] ${isOverdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
      <CalendarClock className="h-3 w-3 shrink-0" />
      <span>{label}:</span>
      <span className="font-mono">{date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
    </div>
  );
};

// Determination / Adjudication status banner
const DeterminationBanner = ({ event, terminology }) => {
  const detStatus = event.determination_status;
  const detDeadline = event.determination_deadline;
  const detNote = event.determination_note;
  const detResult = event.determination_result;
  const daysRemaining = event.determination_days_remaining;
  const dmName = terminology?.decision_maker || 'Decision Maker';

  if (!detStatus && !detDeadline) return null;

  if (detStatus === 'expired') {
    const resultLabel = {
      deemed_rejected: 'DEEMED REJECTED',
      deemed_accepted: 'DEEMED ACCEPTED',
      referable_to_dab: 'REFERABLE TO DAB',
    };
    const resultColor = detResult === 'deemed_accepted' 
      ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
      : detResult === 'referable_to_dab'
      ? 'bg-amber-50 border-amber-300 text-amber-900'
      : 'bg-red-50 border-red-300 text-red-900';
    
    return (
      <div className={`mt-3 p-3 border rounded-sm ${resultColor}`} data-testid="determination-banner-expired">
        <div className="flex items-start gap-2">
          <Gavel className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider">
              {resultLabel[detResult] || 'DETERMINATION EXPIRED'}
            </p>
            <p className="text-[11px] mt-1 leading-relaxed">{detNote}</p>
          </div>
        </div>
      </div>
    );
  }

  if (detDeadline) {
    const deadline = new Date(detDeadline);
    const isUrgent = detStatus === 'urgent';
    const isWarning = detStatus === 'warning';
    return (
      <div className={`mt-2 flex items-center gap-2 text-[11px] ${isUrgent ? 'text-red-600 font-semibold' : isWarning ? 'text-amber-600' : 'text-slate-500'}`} data-testid="determination-banner-pending">
        <Gavel className="h-3 w-3 shrink-0" />
        <span>
          {dmName} must respond by{' '}
          <span className="font-mono font-semibold">{deadline.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          {daysRemaining !== undefined && (
            <span className={`ml-1 ${isUrgent ? 'text-red-700' : isWarning ? 'text-amber-700' : ''}`}>
              ({daysRemaining}d remaining)
            </span>
          )}
        </span>
      </div>
    );
  }

  return null;
};

// FIDIC Interim Claims tracking banner
const FidicInterimBanner = ({ event, onSubmitInterim }) => {
  if (!event.fidic_interim_due) return null;
  
  const nextDue = event.next_interim_claim_due ? new Date(event.next_interim_claim_due) : null;
  const status = event.interim_claim_status;
  const count = event.interim_claim_count || 0;
  const daysRemaining = event.interim_claim_days_remaining;
  const daysOverdue = event.interim_claim_days_overdue;

  const isOverdue = status === 'overdue';
  const isUrgent = status === 'urgent';

  return (
    <div
      className={`mt-3 p-3 border rounded-sm ${
        isOverdue ? 'bg-red-50 border-red-300' : isUrgent ? 'bg-amber-50 border-amber-300' : 'bg-blue-50 border-blue-200'
      }`}
      data-testid="fidic-interim-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1">
          <FileStack className={`h-4 w-4 mt-0.5 shrink-0 ${isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-blue-600'}`} />
          <div>
            <p className={`text-xs font-bold uppercase tracking-wider ${isOverdue ? 'text-red-800' : isUrgent ? 'text-amber-800' : 'text-blue-800'}`}>
              FIDIC Interim Claim {isOverdue ? '- OVERDUE' : isUrgent ? '- DUE SOON' : ''}
            </p>
            <p className="text-[11px] mt-1 text-slate-600">
              {count > 0 ? `${count} interim claim${count > 1 ? 's' : ''} submitted. ` : ''}
              {nextDue && (
                <>
                  Next due: <span className="font-mono font-semibold">{nextDue.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  {isOverdue && daysOverdue !== undefined && <span className="text-red-700 font-semibold ml-1">({daysOverdue}d overdue)</span>}
                  {!isOverdue && daysRemaining !== undefined && <span className="ml-1">({daysRemaining}d remaining)</span>}
                </>
              )}
            </p>
            <p className="text-[10px] mt-0.5 text-slate-500">Per FIDIC Clause 20.1: monthly interim claims while event has continuing effect</p>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onSubmitInterim(event); }}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          data-testid="submit-interim-claim-btn"
        >
          <Send className="h-3 w-3" />
          Submit Interim
        </button>
      </div>
    </div>
  );
};


export default function DelayEventsTab({
  events = [],
  project,
  projectId,
  canCreateEvents,
  canMarkCeased,
  professionalTeam = [],
  internalTeam = [],
  clauseOptions = [],
  contractRules,
  urlFilter,
  getFilterName,
  clearFilter,
  loadProject,
}) {
  const navigate = useNavigate();

  // Contract-specific terminology
  const terminology = contractRules?.terminology || {};
  const noticeTerm = terminology.notice || 'Notice';
  const claimTerm = terminology.claim || 'Claim';
  const decisionMaker = terminology.decision_maker || 'Principal Agent';
  const earlyWarningTerm = terminology.early_warning;
  const isNEC = (project?.contract_type || '').startsWith('NEC');
  const isFIDIC = (project?.contract_type || '').includes('FIDIC');

  // Event filter state
  const [eventSearch, setEventSearch] = useState('');
  const [eventStatusFilter, setEventStatusFilter] = useState('all');
  const [eventTypeFilter, setEventTypeFilter] = useState('all');

  // === Taxonomy state ===
  const [taxonomy, setTaxonomy] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState('');
  const [availableSubTypes, setAvailableSubTypes] = useState([]);

  // Load taxonomy on mount
  React.useEffect(() => {
    api.events.getTaxonomy().then(res => {
      setTaxonomy(res.data?.taxonomy || []);
    }).catch(err => console.warn('Failed to load taxonomy:', err));
  }, []);

  // Add event dialog state
  const [addEventOpen, setAddEventOpen] = useState(false);
  const [formData, setFormData] = useState({
    event_type: '', narrative: '', start_date: '', end_date: '',
    responsible_party: '', responsible_consultant: '', responsible_internal: '',
    selected_clauses: [], has_not_ceased: true,
  });

  // Cease dialog state
  const [ceaseDialogOpen, setCeaseDialogOpen] = useState(false);
  const [selectedEventForCease, setSelectedEventForCease] = useState(null);

  // Interim claim dialog state (FIDIC)
  const [interimDialogOpen, setInterimDialogOpen] = useState(false);
  const [interimEvent, setInterimEvent] = useState(null);
  const [interimForm, setInterimForm] = useState({ description: '', amount: '', days_claimed: '' });
  const [interimSubmitting, setInterimSubmitting] = useState(false);

  const handleOpenInterimDialog = (event) => {
    setInterimEvent(event);
    setInterimForm({
      description: `Interim Claim #${(event.interim_claim_count || 0) + 1} for ${event.event_number || 'Event'}`,
      amount: '',
      days_claimed: '',
    });
    setInterimDialogOpen(true);
  };

  const handleSubmitInterimClaim = async () => {
    if (!interimEvent) return;
    setInterimSubmitting(true);
    try {
      await api.events.submitInterimClaim(interimEvent.id, {
        description: interimForm.description,
        amount: interimForm.amount ? parseFloat(interimForm.amount) : null,
        days_claimed: interimForm.days_claimed ? parseInt(interimForm.days_claimed) : null,
      });
      toast.success('Interim claim submitted successfully');
      setInterimDialogOpen(false);
      if (loadProject) loadProject();
    } catch (error) {
      toast.error('Failed to submit interim claim');
    } finally {
      setInterimSubmitting(false);
    }
  };
  const [ceaseDate, setCeaseDate] = useState('');
  const [ceaseReason, setCeaseReason] = useState('');
  const [ceaseSubmitting, setCeaseSubmitting] = useState(false);

  // Not Ceased dialog state
  const [notCeasedDialogOpen, setNotCeasedDialogOpen] = useState(false);
  const [selectedEventForUpdate, setSelectedEventForUpdate] = useState(null);
  const [updateNotes, setUpdateNotes] = useState('');
  const [updateSubmitting, setUpdateSubmitting] = useState(false);

  // Derived: unique event families and types for filter dropdowns
  const eventTypes = [...new Set(events.map(e => e.event_type).filter(Boolean))];
  const eventFamilies = [...new Set(events.map(e => e.event_family).filter(Boolean))];

  // Filtered events
  const filteredEvents = events.filter(event => {
    if (eventSearch) {
      const search = eventSearch.toLowerCase();
      const matchesSearch =
        event.event_type?.toLowerCase().includes(search) ||
        event.narrative?.toLowerCase().includes(search) ||
        event.responsible_party?.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    if (eventStatusFilter !== 'all') {
      if (eventStatusFilter === 'open' && (event.status === 'Closed' || event.status === 'Resolved')) return false;
      if (eventStatusFilter === 'draft' && event.status !== 'Draft') return false;
      if (eventStatusFilter === 'pending' && event.status !== 'Pending Approval') return false;
      if (eventStatusFilter === 'closed' && event.status !== 'Closed' && event.status !== 'Resolved') return false;
    }
    if (eventTypeFilter !== 'all') {
      // Check both event_family and event_type for backward compat
      if (event.event_family !== eventTypeFilter && event.event_type !== eventTypeFilter) return false;
    }
    if (urlFilter && urlFilter !== 'all') {
      if (urlFilter === 'open') return event.status !== 'Closed' && event.status !== 'Resolved';
      if (urlFilter === 'draft') return event.status === 'Draft' || event.status === 'Open';
      if (urlFilter === 'awaiting_action') return event.status === 'Draft' || event.status === 'Pending Approval';
    }
    return true;
  });

  const handleClauseToggle = (clauseValue, parentClause = null) => {
    setFormData(prev => {
      let newClauses = [...prev.selected_clauses];
      if (newClauses.includes(clauseValue)) {
        newClauses = newClauses.filter(c => c !== clauseValue);
      } else {
        newClauses.push(clauseValue);
        if (parentClause && !newClauses.includes(parentClause)) {
          newClauses.push(parentClause);
        }
      }
      return { ...prev, selected_clauses: newClauses };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let responsiblePartyFull = formData.responsible_party;
      if (formData.responsible_party === 'Consultant' && formData.responsible_consultant) {
        const consultant = professionalTeam.find(m => m.id === formData.responsible_consultant);
        if (consultant) {
          responsiblePartyFull = `${consultant.discipline || 'Consultant'} - ${consultant.company_name || consultant.contact_name}`;
        }
      } else if (formData.responsible_party === 'Internal' && formData.responsible_internal) {
        const member = internalTeam.find(m => m.id === formData.responsible_internal);
        if (member) {
          responsiblePartyFull = `Internal - ${member.name || member.role}`;
        }
      }
      const data = {
        ...formData,
        event_family: selectedFamily || formData.event_family || undefined,
        event_sub_type: formData.event_sub_type || formData.event_type || undefined,
        responsible_party: responsiblePartyFull,
        responsible_consultant_id: formData.responsible_consultant || null,
        responsible_internal_id: formData.responsible_internal || null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      };
      await api.events.create(projectId, data);
      toast.success('Delay event created successfully');
      setAddEventOpen(false);
      setSelectedFamily('');
      setAvailableSubTypes([]);
      setFormData({ event_type: '', event_family: '', event_sub_type: '', narrative: '', start_date: '', end_date: '', responsible_party: '', responsible_consultant: '', responsible_internal: '', selected_clauses: [], has_not_ceased: true });
      loadProject();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const handleOpenCeaseDialog = (event, e) => {
    if (e) e.stopPropagation();
    setSelectedEventForCease(event);
    setCeaseDate(new Date().toISOString().split('T')[0]);
    setCeaseReason('');
    setCeaseDialogOpen(true);
  };

  const handleSubmitCease = async () => {
    if (!ceaseDate) { toast.error('Please select a cease date'); return; }
    if (!ceaseReason.trim()) { toast.error('Please provide a reason for the delay ceasing'); return; }
    setCeaseSubmitting(true);
    try {
      await api.delayMonitoring.markCeased(selectedEventForCease.id, {
        cease_date: ceaseDate, cease_reason: ceaseReason, attachments: []
      });
      toast.success('Delay recorded as ceased. Claim deadline has been calculated.');
      setCeaseDialogOpen(false);
      loadProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as ceased');
    } finally { setCeaseSubmitting(false); }
  };

  const handleOpenNotCeasedDialog = (event, e) => {
    if (e) e.stopPropagation();
    setSelectedEventForUpdate(event);
    setUpdateNotes('');
    setNotCeasedDialogOpen(true);
  };

  const handleSubmitNotCeased = async () => {
    if (!updateNotes.trim()) { toast.error('Please provide an update on the delay status'); return; }
    setUpdateSubmitting(true);
    try {
      await api.delayMonitoring.addUpdate(selectedEventForUpdate.id, {
        update_type: 'not_ceased', reason: updateNotes, attachments: []
      });
      toast.success('Delay status update recorded successfully');
      setNotCeasedDialogOpen(false);
      loadProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record update');
    } finally { setUpdateSubmitting(false); }
  };

  return (
    <>
      {/* Filter Banner */}
      {urlFilter && getFilterName(urlFilter) && (
        <FilterBanner
          filterName={getFilterName(urlFilter)}
          count={filteredEvents.length}
          onClear={clearFilter}
          color="amber"
        />
      )}

      {/* Back Button */}
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="mb-4 text-slate-600 hover:text-slate-900"
        data-testid="events-back-button"
      >
        &larr; Back to Dashboard
      </Button>

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-barlow font-semibold uppercase">{isNEC ? 'Compensation Events' : 'Delay Events'}</h2>
        <div className="flex gap-2">
          <PrintScheduleButton moduleType="DelayEvent" data={events} project={project} />
          {canCreateEvents && (
            <Dialog open={addEventOpen} onOpenChange={setAddEventOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-delay-event-button" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3 active:scale-95 transition-transform">
                  <Plus className="h-4 w-4 mr-2" />
                  {isNEC ? 'Add Compensation Event' : 'Add Delay Event'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
                <DialogHeader className="shrink-0">
                  <DialogTitle className="text-xl font-barlow font-bold uppercase">{isNEC ? 'Add Compensation Event' : 'Add Delay Event'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-2">
                  {/* Event Family (Primary) */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Event Family *</Label>
                    <Select 
                      value={selectedFamily} 
                      onValueChange={(value) => {
                        setSelectedFamily(value);
                        const familyData = taxonomy.find(t => t.family === value);
                        setAvailableSubTypes(familyData?.sub_types || []);
                        setFormData(prev => ({ ...prev, event_type: '', event_family: value, event_sub_type: '' }));
                      }}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="event-family-select">
                        <SelectValue placeholder="Select event family..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {taxonomy.map(t => (
                          <SelectItem key={t.family} value={t.family}>
                            {t.family}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedFamily && (
                      <p className="text-xs text-slate-500 mt-1">
                        {taxonomy.find(t => t.family === selectedFamily)?.description}
                      </p>
                    )}
                  </div>

                  {/* Event Sub-Type */}
                  {selectedFamily && availableSubTypes.length > 0 && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Event Sub-Type *</Label>
                      <Select 
                        value={formData.event_type || ''} 
                        onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value, event_sub_type: value }))}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="event-subtype-select">
                          <SelectValue placeholder="Select specific event type..." />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {availableSubTypes.map(st => (
                            <SelectItem key={st} value={st}>{st}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Responsible Party</Label>
                    <Select value={formData.responsible_party} onValueChange={(value) => setFormData({ ...formData, responsible_party: value, responsible_consultant: '', responsible_internal: '' })}>
                      <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select responsible party" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Client">Client / Employer</SelectItem>
                        <SelectItem value="Contractor">Contractor</SelectItem>
                        <SelectItem value="Consultant">Consultant / Professional Team</SelectItem>
                        <SelectItem value="Internal">Internal Team</SelectItem>
                        <SelectItem value="Third Party">Third Party</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.responsible_party === 'Consultant' && professionalTeam.length > 0 && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Select Consultant</Label>
                      <Select value={formData.responsible_consultant} onValueChange={(value) => setFormData({ ...formData, responsible_consultant: value })}>
                        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select specific consultant" /></SelectTrigger>
                        <SelectContent>
                          {professionalTeam.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.discipline || 'Consultant'} - {member.company_name || member.contact_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {formData.responsible_party === 'Internal' && internalTeam.length > 0 && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Select Team Member</Label>
                      <Select value={formData.responsible_internal} onValueChange={(value) => setFormData({ ...formData, responsible_internal: value })}>
                        <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select team member" /></SelectTrigger>
                        <SelectContent>
                          {internalTeam.map((member) => (
                            <SelectItem key={member.id} value={member.id}>
                              {member.name} - {member.role || 'Team Member'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Start Date</Label>
                      <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required className="rounded-sm font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">End Date (Optional)</Label>
                      <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="rounded-sm font-mono" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Narrative</Label>
                    <Textarea value={formData.narrative} onChange={(e) => setFormData({ ...formData, narrative: e.target.value })} required rows={6} placeholder="Describe the delay event in detail..." className="rounded-sm" />
                  </div>

                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">
                      {contractRules?.short_name || 'Contract'} Clauses
                    </Label>
                    <p className="text-xs text-slate-600 mb-3">
                      {isNEC ? 'Select the applicable compensation event clause:' :
                       isFIDIC ? 'Select applicable FIDIC clauses for entitlement:' :
                       'Select applicable clauses for Extension of Time entitlement:'}
                    </p>
                    <div className="border border-slate-200 rounded-sm p-4 max-h-48 sm:max-h-80 overflow-y-auto space-y-4">
                      {clauseOptions.map((mainClause) => (
                        <div key={mainClause.value} className="border border-slate-100 rounded-md bg-slate-50/50">
                          <div className="flex items-start space-x-3 p-3 bg-slate-100/80 rounded-t-md border-b border-slate-200">
                            <Checkbox id={mainClause.value} checked={formData.selected_clauses.includes(mainClause.value)} onCheckedChange={() => handleClauseToggle(mainClause.value)} className="mt-0.5" />
                            <label htmlFor={mainClause.value} className="text-sm font-semibold text-slate-800 cursor-pointer leading-tight flex-1">{mainClause.label}</label>
                          </div>
                          {mainClause.sub_clauses && mainClause.sub_clauses.length > 0 && (
                            <div className="p-3 pl-8 space-y-2">
                              {mainClause.sub_clauses.map((subClause) => (
                                <div key={subClause.value} className="flex items-start space-x-2">
                                  <Checkbox id={subClause.value} checked={formData.selected_clauses.includes(subClause.value)} onCheckedChange={() => handleClauseToggle(subClause.value, mainClause.value)} className="mt-0.5" />
                                  <label htmlFor={subClause.value} className="text-xs text-slate-600 cursor-pointer leading-tight">{subClause.label}</label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      {isNEC ? 'Note: Compensation event procedures per Clause 61-65 apply' :
                       isFIDIC ? 'Note: Clause 20.1 time-bar procedure applies to all claims' :
                       'Note: Clauses 23.4, 23.4.1 & 23.4.2 will be automatically included in the notice'}
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <Checkbox id="has_not_ceased" checked={formData.has_not_ceased !== false} onCheckedChange={(checked) => setFormData({ ...formData, has_not_ceased: checked })} className="mt-0.5" />
                      <div>
                        <label htmlFor="has_not_ceased" className="text-sm font-medium text-amber-800 cursor-pointer">Delay has not yet ceased</label>
                        <p className="text-xs text-amber-600 mt-1">Keep this checked if the delay is still ongoing. Weekly reminders will be sent until the delay ceases.</p>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3">
                    Create Event
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search events..." value={eventSearch} onChange={(e) => setEventSearch(e.target.value)} className="pl-9" data-testid="event-search" />
        </div>
        <Select value={eventStatusFilter} onValueChange={setEventStatusFilter}>
          <SelectTrigger className="w-40" data-testid="event-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending">Pending Approval</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={eventTypeFilter} onValueChange={setEventTypeFilter}>
          <SelectTrigger className="w-52" data-testid="event-type-filter"><SelectValue placeholder="All Families" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Families</SelectItem>
            {eventFamilies.map(fam => (
              <SelectItem key={fam} value={fam}>{fam}</SelectItem>
            ))}
            {/* Also show legacy types that don't have family yet */}
            {eventTypes.filter(t => !eventFamilies.includes(t)).map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(eventSearch || eventStatusFilter !== 'all' || eventTypeFilter !== 'all') && (
          <Button variant="ghost" size="sm" onClick={() => { setEventSearch(''); setEventStatusFilter('all'); setEventTypeFilter('all'); }} className="text-slate-500">
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-2xl font-bold text-slate-900">{events.length}</p>
          <p className="text-xs text-slate-500 uppercase font-medium">Total Events</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-2xl font-bold text-blue-600">{events.filter(e => e.status !== 'Closed' && e.status !== 'Resolved').length}</p>
          <p className="text-xs text-slate-500 uppercase font-medium">Open</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-2xl font-bold text-amber-600">{events.filter(e => e.status === 'Draft').length}</p>
          <p className="text-xs text-slate-500 uppercase font-medium">Draft</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-2xl font-bold text-green-600">{events.filter(e => e.status === 'Closed' || e.status === 'Resolved').length}</p>
          <p className="text-xs text-slate-500 uppercase font-medium">Closed</p>
        </div>
        <div className="bg-white border border-red-200 rounded-lg p-4" data-testid="time-barred-stat">
          <p className="text-2xl font-bold text-red-600">
            {events.filter(e => e.notice_status_flag === 'time_barred' || e.claim_status_flag === 'time_barred').length}
          </p>
          <p className="text-xs text-red-500 uppercase font-medium">Time Barred</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-lg p-4" data-testid="at-risk-stat">
          <p className="text-2xl font-bold text-amber-600">
            {events.filter(e => e.notice_status_flag === 'urgent' || e.notice_status_flag === 'warning' || e.claim_status_flag === 'urgent' || e.claim_status_flag === 'warning').length}
          </p>
          <p className="text-xs text-amber-500 uppercase font-medium">At Risk</p>
        </div>
        <div className="bg-white border border-purple-200 rounded-lg p-4" data-testid="deemed-stat">
          <p className="text-2xl font-bold text-purple-600">
            {events.filter(e => e.deemed_result_applied).length}
          </p>
          <p className="text-xs text-purple-500 uppercase font-medium">Deemed</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-lg p-4" data-testid="pending-classification-stat">
          <p className="text-2xl font-bold text-amber-600">
            {events.filter(e => e.notice_generation_locked || e.classification_status === 'pending' || e.status === 'pending_classification').length}
          </p>
          <p className="text-xs text-amber-500 uppercase font-medium">Pending Classification</p>
        </div>
      </div>

      {/* Event Approval Widget */}
      <EventApprovalWidget projectId={projectId} onApprovalChange={loadProject} />

      {/* Events List */}
      {filteredEvents.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
          <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="text-xl font-barlow font-bold uppercase mb-2">
            {urlFilter ? 'No Matching Events' : isNEC ? 'No Compensation Events' : 'No Delay Events'}
          </h3>
          <p className="text-slate-600">
            {urlFilter ? 'No events match the current filter' : 'Create your first delay event to start tracking'}
          </p>
          {urlFilter && (
            <Button variant="outline" onClick={clearFilter} className="mt-4">Clear Filter</Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className={`bg-white border rounded-sm shadow-sm hover:shadow-md transition-all p-6 ${
                event.notice_status_flag === 'time_barred' || event.claim_status_flag === 'time_barred'
                  ? 'border-red-300 bg-red-50/20 border-l-4 border-l-red-500' :
                event.notice_status_flag === 'urgent' || event.claim_status_flag === 'urgent'
                  ? 'border-orange-200 bg-orange-50/20' :
                event.cease_status === 'ceased_confirmed' ? 'border-green-200 bg-green-50/30' :
                event.cease_status === 'ceased_pending' ? 'border-amber-200 bg-amber-50/30' :
                'border-slate-200'
              }`}
              data-testid={`event-card-${event.id}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1 cursor-pointer" onClick={() => navigate(`/events/${event.id}`)}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg font-bold hover:text-secondary transition-colors">
                      {event.event_number || event.event_type}
                    </h3>
                    {event.cease_status === 'ceased_confirmed' ? (
                      <Badge className="bg-green-100 text-green-700 border-green-300 border">CEASED - CONFIRMED</Badge>
                    ) : event.cease_status === 'ceased_pending' ? (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300 border">CEASED - PENDING CONFIRMATION</Badge>
                    ) : (() => {
                      const displayStatus = getEventDisplayStatus(event);
                      return displayStatus ? (
                        <Badge className={`${getStatusBadge(displayStatus.status)} border`}>
                          {displayStatus.label}
                        </Badge>
                      ) : null;
                    })()}
                    {event.has_not_ceased && event.cease_status !== 'ceased_confirmed' && event.cease_status !== 'ceased_pending' && !isClaimSubmitted(event) && (
                      <Badge className="bg-orange-100 text-orange-700 border-orange-200 border text-xs">
                        <Timer className="h-3 w-3 mr-1" />
                        {event.days_ongoing || Math.ceil((new Date() - new Date(event.start_date || event.occurrence_date)) / (1000 * 60 * 60 * 24))} days
                      </Badge>
                    )}
                    {/* Event source badge for non-manual events */}
                    {event.event_source && event.event_source !== 'manual' && (
                      <Badge className="bg-slate-100 text-slate-600 border-slate-200 border text-xs uppercase">
                        Source: {event.event_source.replace(/_/g, ' ')}
                      </Badge>
                    )}
                    {/* Compliance badges */}
                    {event.notice_status_flag && (
                      <ComplianceBadge
                        flag={event.notice_status_flag}
                        daysRemaining={event.notice_days_remaining}
                        daysOverdue={event.notice_days_overdue}
                        label={noticeTerm}
                      />
                    )}
                    {event.claim_status_flag && !isClaimSubmitted(event) && (
                      <ComplianceBadge
                        flag={event.claim_status_flag}
                        daysRemaining={event.claim_days_remaining}
                        daysOverdue={event.claim_days_overdue}
                        label={claimTerm}
                      />
                    )}
                  </div>
                  {event.event_number && (
                    <p className="text-sm text-slate-500 mb-1">
                      {event.event_family ? (
                        <>
                          <span className="font-medium text-slate-600">{event.event_family}</span>
                          {event.event_sub_type && event.event_sub_type !== event.event_family && (
                            <span className="text-slate-400"> / {event.event_sub_type}</span>
                          )}
                        </>
                      ) : (
                        event.event_type
                      )}
                    </p>
                  )}
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">{event.narrative}</p>

                  {/* Deadline rows */}
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mb-2">
                    <DeadlineInfo label={`${noticeTerm} Deadline`} deadline={event.notice_deadline} statusFlag={event.notice_status_flag} />
                    {!isClaimSubmitted(event) && (
                      <DeadlineInfo label={`${claimTerm} Deadline`} deadline={event.claim_deadline} statusFlag={event.claim_status_flag} />
                    )}
                    {isClaimSubmitted(event) && event.determination_deadline && (
                      <DeadlineInfo label={`${decisionMaker} Response Deadline`} deadline={event.determination_deadline} statusFlag={event.determination_status === 'expired' ? 'time_barred' : event.determination_status} />
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span className="font-mono">{(event.start_date || event.occurrence_date) ? new Date(event.start_date || event.occurrence_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    <span>Responsible: {event.responsible_party || 'Not specified'}</span>
                  </div>

                  {/* Risk Flags */}
                  <RiskFlags flags={event.risk_flags} />

                  {/* Determination / Adjudication Status */}
                  <DeterminationBanner event={event} terminology={event.terminology} />

                  {/* FIDIC Interim Claims Tracking */}
                  {isFIDIC && (
                    <FidicInterimBanner event={event} onSubmitInterim={handleOpenInterimDialog} />
                  )}

                  {(event.cease_status === 'ceased_pending' || event.cease_status === 'ceased_confirmed') && event.delay_ceased_date && (
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      <span className="text-green-700 font-medium">
                        Delay marked as ceased on {new Date(event.delay_ceased_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        {event.cease_status === 'ceased_pending' && <span className="text-amber-600 ml-1">(Awaiting confirmation)</span>}
                        {event.cease_status === 'ceased_confirmed' && <span className="text-green-600 ml-1">(Confirmed)</span>}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap lg:flex-nowrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.id}?tab=timeline`); }} className="text-xs">
                    <History className="h-3 w-3 mr-1" /> Timeline
                  </Button>
                  {canMarkCeased && event.cease_status !== 'ceased_pending' && event.cease_status !== 'ceased_confirmed' && !isClaimSubmitted(event) && (
                    <>
                      <Button size="sm" variant="outline" onClick={(e) => handleOpenNotCeasedDialog(event, e)} className="text-xs text-amber-600 border-amber-200 hover:bg-amber-50">
                        <AlertTriangle className="h-3 w-3 mr-1" /> Not Ceased
                      </Button>
                      <Button size="sm" onClick={(e) => handleOpenCeaseDialog(event, e)} className="text-xs bg-green-600 hover:bg-green-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Record Delay Ceased
                      </Button>
                    </>
                  )}
                  {event.cease_status === 'ceased_pending' && (
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Delay Ceased
                    </Badge>
                  )}
                  {event.cease_status === 'ceased_confirmed' && (
                    <Badge className="bg-green-100 text-green-700 border-green-200">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Ceased &amp; Confirmed
                    </Badge>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => navigate(`/events/${event.id}`)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mark Ceased Dialog */}
      <Dialog open={ceaseDialogOpen} onOpenChange={setCeaseDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">Record Delay Ceased</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Confirm that the delay event has ceased. This will notify the Project Manager for confirmation.</p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Cease Date</Label>
              <Input type="date" value={ceaseDate} onChange={(e) => setCeaseDate(e.target.value)} className="rounded-sm font-mono" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Reason / Notes</Label>
              <Textarea value={ceaseReason} onChange={(e) => setCeaseReason(e.target.value)} placeholder="Describe why the delay has ceased..." rows={4} className="rounded-sm" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setCeaseDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitCease} disabled={ceaseSubmitting} className="bg-green-600 hover:bg-green-700">
                {ceaseSubmitting ? 'Recording...' : 'Record Delay Ceased'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Not Ceased (Update) Dialog */}
      <Dialog open={notCeasedDialogOpen} onOpenChange={setNotCeasedDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">Delay Status Update</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Provide an update on the ongoing delay. This will be recorded in the delay timeline.</p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Update Notes</Label>
              <Textarea value={updateNotes} onChange={(e) => setUpdateNotes(e.target.value)} placeholder="Describe the current status of the delay..." rows={4} className="rounded-sm" />
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setNotCeasedDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitNotCeased} disabled={updateSubmitting} className="bg-amber-600 hover:bg-amber-700">
                {updateSubmitting ? 'Submitting...' : 'Submit Update'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* FIDIC Interim Claim Dialog */}
      <Dialog open={interimDialogOpen} onOpenChange={setInterimDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-barlow font-bold uppercase">Submit Interim Claim</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Per FIDIC Clause 20.1, submit a monthly interim claim while the delay event has continuing effect.
              {interimEvent && (
                <span className="block mt-1 font-medium text-slate-800">
                  Event: {interimEvent.event_number || interimEvent.event_type}
                </span>
              )}
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Description</Label>
              <Textarea
                value={interimForm.description}
                onChange={(e) => setInterimForm({ ...interimForm, description: e.target.value })}
                placeholder="Describe the current status of the ongoing delay and claim..."
                rows={3}
                className="rounded-sm"
                data-testid="interim-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Days Claimed (EOT)</Label>
                <Input
                  type="number"
                  min="0"
                  value={interimForm.days_claimed}
                  onChange={(e) => setInterimForm({ ...interimForm, days_claimed: e.target.value })}
                  placeholder="0"
                  className="rounded-sm"
                  data-testid="interim-days"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Amount (ZAR)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={interimForm.amount}
                  onChange={(e) => setInterimForm({ ...interimForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="rounded-sm"
                  data-testid="interim-amount"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setInterimDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSubmitInterimClaim}
                disabled={interimSubmitting}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="interim-submit-btn"
              >
                {interimSubmitting ? 'Submitting...' : 'Submit Interim Claim'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
